/**
 * CourierSettingsPanel — Admin UI untuk Shipping Settings v2.
 *
 * Fitur:
 *  - List service kurir yang sudah ter-discover dari /api/shipping/rates
 *  - Toggle aktif/nonaktif per service
 *  - Atur priority (number input)
 *  - Mark recommended
 *  - Reset ke default (kosongkan setting → fallback "show all")
 *
 * Backend endpoints:
 *  - GET  /api/admin/shipping/courier-settings
 *  - PUT  /api/admin/shipping/courier-settings  body: { enabled_couriers: [...] }
 *  - POST /api/admin/shipping/courier-settings/reset
 */
import React, { useEffect, useState } from "react";
import { Save, Truck, Loader2, Star, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useIsFeatureEnabled } from "@/hooks/useFeatureFlag";

const CATEGORY_LABELS = {
  instant: "Instant",
  sameday: "Same Day",
  nextday: "Next Day",
  regular: "Regular",
  economy: "Economy",
  other: "Lainnya",
};

const CATEGORY_COLORS = {
  instant: "bg-blue-50 text-blue-700 border-blue-200",
  sameday: "bg-purple-50 text-purple-700 border-purple-200",
  nextday: "bg-amber-50 text-amber-700 border-amber-200",
  regular: "bg-emerald-50 text-emerald-700 border-emerald-200",
  economy: "bg-slate-50 text-slate-700 border-slate-200",
  other: "bg-gray-50 text-gray-600 border-gray-200",
};

export default function CourierSettingsPanel() {
  const jubelioEnabled = useIsFeatureEnabled("integration.jubelio");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/shipping/couriers");
      setItems(data?.enabled_couriers || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const syncFromJubelio = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/shipping/couriers/sync");
      const src = data?.source === "mock" ? " (fallback mock, Jubelio sedang error)" : "";
      toast.success(`Sync sukses: ${data?.new_count || 0} baru, ${data?.updated_count || 0} sudah ada${src}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const upd = (idx, key, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/shipping/courier-settings", { enabled_couriers: items });
      toast.success("Pengaturan kurir berhasil disimpan.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset ke default? Semua pengaturan kurir akan dihapus dan checkout akan menampilkan semua layanan kurir.")) return;
    setSaving(true);
    try {
      await api.post("/admin/shipping/courier-settings/reset");
      toast.success("Pengaturan kurir direset");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="courier-settings-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-bold flex items-center gap-1.5 text-[#1a1a1a]">
            <Truck size={16} /> Jasa Kirim yang Ditampilkan ke Customer
          </h3>
          <p className="text-xs text-[#5C5C5C] mt-0.5">
            Aktifkan/nonaktifkan layanan, atur urutan, dan tandai rekomendasi.
            {jubelioEnabled && <> Klik <b>Sync Kurir dari Jubelio</b> untuk mengambil daftar layanan terbaru.</>}
          </p>
        </div>
        <div className="flex gap-2">
          {jubelioEnabled && (
          <Button onClick={syncFromJubelio} size="sm" className="bg-[#2D6A4F] hover:bg-[#1B5E20] rounded-lg text-white" disabled={syncing || saving} data-testid="courier-sync-btn">
            {syncing ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
            {syncing ? "Sync..." : "Sync Kurir dari Jubelio"}
          </Button>
          )}
          <Button onClick={reset} variant="outline" size="sm" className="rounded-lg" disabled={saving || syncing} data-testid="courier-reset-btn">
            <RotateCcw size={12} className="mr-1" /> Reset
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#5C5C5C] py-6 justify-center">
          <Loader2 size={14} className="animate-spin" /> Memuat...
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[#5C5C5C] text-center py-8 border border-dashed border-[#E5E5E5] rounded-lg space-y-3" data-testid="courier-empty-state">
          <div className="font-bold text-[#1a1a1a]">Belum ada daftar kurir.</div>
          {jubelioEnabled ? (
            <>
              <div className="text-xs">
                Klik <b>&quot;Sync Kurir dari Jubelio&quot;</b> untuk mengambil daftar
                layanan dari data ongkir terbaru.
              </div>
              <Button onClick={syncFromJubelio} className="bg-[#2D6A4F] hover:bg-[#1B5E20] rounded-lg text-white mx-auto" disabled={syncing} data-testid="courier-sync-empty-btn">
                {syncing ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />}
                {syncing ? "Sync..." : "Sync Kurir dari Jubelio"}
              </Button>
            </>
          ) : (
            <div className="text-xs">
              Tambahkan tarif flat manual di section <b>Fallback Tarif Flat</b>
              untuk memulai. Sync realtime tidak tersedia di paket ini.
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div
              key={it.key || i}
              className={`grid grid-cols-12 gap-2 items-center p-2 rounded-lg border ${it.enabled === false ? "bg-gray-50 border-gray-200 opacity-70" : "bg-white border-[#E5E5E5]"}`}
              data-testid={`courier-row-${it.key}`}
            >
              <div className="col-span-1 flex justify-center">
                <input
                  type="checkbox"
                  checked={it.enabled !== false}
                  onChange={(e) => upd(i, "enabled", e.target.checked)}
                  className="w-4 h-4"
                  data-testid={`courier-enabled-${it.key}`}
                  aria-label={`Aktifkan ${it.label}`}
                />
              </div>
              <div className="col-span-4">
                <div className="font-bold text-sm text-[#1a1a1a]">{it.label || `${it.courier_name} ${it.service_name}`}</div>
                <div className="text-[10px] text-[#5C5C5C] font-mono">{it.key}</div>
              </div>
              <div className="col-span-2">
                {it.enabled !== false ? (
                  <span className="text-[10px] font-bold px-2 py-1 rounded border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`courier-status-${it.key}`}>
                    AKTIF
                  </span>
                ) : (
                  <span className="text-[10px] font-bold px-2 py-1 rounded border bg-slate-100 text-slate-600 border-slate-300" data-testid={`courier-status-${it.key}`}>
                    NONAKTIF
                  </span>
                )}
              </div>
              <div className="col-span-1">
                <span className={`text-[10px] font-bold px-2 py-1 rounded border ${CATEGORY_COLORS[it.category] || CATEGORY_COLORS.other}`}>
                  {CATEGORY_LABELS[it.category] || it.category || "—"}
                </span>
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  value={it.priority ?? 0}
                  onChange={(e) => upd(i, "priority", Number(e.target.value) || 0)}
                  className="h-8 text-xs"
                  data-testid={`courier-priority-${it.key}`}
                  aria-label="Prioritas"
                />
              </div>
              <div className="col-span-2 flex justify-center">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!it.recommended}
                    onChange={(e) => upd(i, "recommended", e.target.checked)}
                    className="w-4 h-4"
                    data-testid={`courier-recommended-${it.key}`}
                  />
                  <Star size={12} className={it.recommended ? "text-amber-500 fill-amber-500" : "text-gray-300"} />
                </label>
              </div>
            </div>
          ))}

          <div className="grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C] px-2 pt-1">
            <div className="col-span-1 text-center">Aktif</div>
            <div className="col-span-4">Layanan</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1">Kategori</div>
            <div className="col-span-2">Prioritas</div>
            <div className="col-span-2 text-center">Rekomendasi</div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          onClick={save}
          disabled={saving || loading || items.length === 0}
          className="bg-[#E85D04] hover:bg-[#DC2F02] rounded-xl"
          data-testid="courier-save-btn"
        >
          <Save size={14} className="mr-1" /> {saving ? "Menyimpan..." : "Simpan Pengaturan Kurir"}
        </Button>
      </div>
    </Card>
  );
}
